import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CTA_OPTIONS, CENTRES } from "@/data/site";
import { CATEGORIES, getFormationsByCategory, FORMATIONS } from "@/data/formations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadContext = createContext(null);
export const useLead = () => useContext(LeadContext);

const TITLES = {
  devis: "Demander un devis",
  rappel: "Être rappelé",
  inscription: "S'inscrire à une formation",
  brochure: "Télécharger la brochure",
  contact: "Nous contacter",
  document: "Recevoir un document",
};

const SUBTITLES = {
  devis: "Recevez une proposition personnalisée sous 48h ouvrées.",
  rappel: "Laissez vos coordonnées, un conseiller vous rappelle rapidement.",
  inscription: "Réservez votre place, nous vous accompagnons dans votre dossier.",
  brochure: "Renseignez vos coordonnées pour recevoir notre brochure complète.",
  contact: "Une question ? Notre équipe vous répond.",
  document: "Indiquez votre e-mail : le document vous sera envoyé par nos soins.",
};

export function LeadModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("devis");
  const [formation, setFormation] = useState("");
  const [docTitle, setDocTitle] = useState("");
  const [centre, setCentre] = useState("");
  const [objet, setObjet] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "" });

  // Centres compatibles avec la formation sélectionnée (filtrage par catégorie).
  const compatibleCentres = useMemo(() => {
    if (!formation) return CENTRES;
    const f = FORMATIONS.find((x) => x.title === formation);
    if (!f) return CENTRES;
    const isEnseignant = f.category === "formations-enseignants";
    const isTransport =
      f.category === "transport-de-marchandises" || f.category === "transport-de-voyageurs";
    return CENTRES.filter((c) => {
      const tags = (Array.isArray(c.tag) ? c.tag : [c.tag]).map((t) => (t || "").toLowerCase());
      if (isEnseignant) return tags.some((t) => t.includes("enseignant"));
      if (isTransport) return tags.some((t) => t.includes("groupe lourd"));
      return true;
    });
  }, [formation]);

  // Catégories de formation proposées par le centre sélectionné.
  const centreCategories = useMemo(() => {
    if (!centre) return null;
    const c = CENTRES.find((x) => x.ville === centre);
    if (!c) return null;
    const tags = (Array.isArray(c.tag) ? c.tag : [c.tag]).map((t) => (t || "").toLowerCase());
    const cats = new Set();
    if (tags.some((t) => t.includes("enseignant"))) cats.add("formations-enseignants");
    if (tags.some((t) => t.includes("groupe lourd"))) {
      cats.add("transport-de-marchandises");
      cats.add("transport-de-voyageurs");
    }
    return cats;
  }, [centre]);

  // Si le centre choisi n'est plus compatible avec la nouvelle formation, on le réinitialise.
  useEffect(() => {
    if (centre && !compatibleCentres.some((c) => c.ville === centre)) {
      setCentre("");
    }
  }, [compatibleCentres, centre]);

  // Si la formation choisie n'est plus proposée par le nouveau centre, on la réinitialise.
  useEffect(() => {
    if (!centreCategories || !formation) return;
    const f = FORMATIONS.find((x) => x.title === formation);
    if (f && !centreCategories.has(f.category)) {
      setFormation("");
    }
  }, [centreCategories, formation]);

  const openLead = useCallback((t = "devis", opts = {}) => {
    setType(t);
    setFormation(opts.formation || "");
    setDocTitle(opts.document || "");
    setCentre(opts.centre || "");
    setObjet(opts.objet || "");
    setForm({ nom: "", email: "", telephone: "", message: opts.message || "" });
    setDone(false);
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom) {
      toast.error("Merci d'indiquer votre nom.");
      return;
    }
    if (type === "document" && !form.email) {
      toast.error("Merci d'indiquer votre e-mail pour recevoir le document.");
      return;
    }
    if (type === "document" && !centre) {
      toast.error("Merci de sélectionner le centre concerné.");
      return;
    }
    if (!form.email && !form.telephone) {
      toast.error("Merci d'indiquer un moyen de contact (e-mail ou téléphone).");
      return;
    }
    setSubmitting(true);
    try {
      const message = objet
        ? `[${objet}] ${
            type === "document" && docTitle
              ? `Demande de document : ${docTitle}.${form.message ? " " + form.message : ""}`
              : form.message
          }`.trim()
        : type === "document" && docTitle
          ? `Demande de document : ${docTitle}.${form.message ? " " + form.message : ""}`
          : form.message;
      const matched = type !== "document" ? FORMATIONS.find((f) => f.title === formation) : null;
      const categorie = matched ? matched.category : "";
      await axios.post(`${API}/leads`, {
        type,
        nom: form.nom,
        email: form.email || undefined,
        telephone: form.telephone,
        formation: type === "document" ? docTitle : formation,
        centre,
        categorie,
        message,
        consent: true,
      });
      // Google Ads — conversion "Formulaire devis ou rappel envoyé"
      // Se déclenche UNIQUEMENT après confirmation serveur (201), pour devis/rappel, sans PII.
      if (
        (type === "devis" || type === "rappel") &&
        typeof window !== "undefined" &&
        typeof window.gtag === "function"
      ) {
        window.gtag("event", "conversion", {
          send_to: "AW-18326109952/GMjGCKXI4-gcEID-yKJE",
        });
      }
      setDone(true);
      toast.success("Votre demande a bien été envoyée.");
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LeadContext.Provider value={{ openLead }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lead-modal"
          >
            <div
              className="absolute inset-0 bg-brand-blue/50 backdrop-blur-sm"
              onClick={close}
            />
            <motion.div
              className="relative z-10 w-full max-w-lg rounded-3xl bg-white p-8 shadow-[0_25px_50px_-12px_rgba(0,35,102,0.25)]"
              initial={{ y: 40, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={close}
                className="absolute right-5 top-5 text-slate-400 transition-colors hover:text-brand-red"
                aria-label="Fermer"
                data-testid="lead-modal-close"
              >
                <X className="h-5 w-5" />
              </button>

              {done ? (
                <div className="py-8 text-center">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-brand-red" />
                  <h3 className="mt-5 font-display text-2xl font-bold text-brand-blue">
                    Merci {form.nom.split(" ")[0]} !
                  </h3>
                  <p className="mt-2 text-slate-500">
                    {type === "document" && docTitle
                      ? `Votre demande a bien été enregistrée. Le document « ${docTitle} » vous sera envoyé par e-mail par nos soins.`
                      : "Votre demande a bien été enregistrée. Notre équipe revient vers vous très vite."}
                  </p>
                  <Button
                    className="mt-6 rounded-full bg-brand-blue hover:bg-brand-blue-light"
                    onClick={close}
                    data-testid="lead-modal-done"
                  >
                    Fermer
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
                    EDUCASER GROUP
                  </span>
                  <h3 className="mt-2 font-display text-2xl font-bold text-brand-blue">
                    {TITLES[type]}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">{SUBTITLES[type]}</p>

                  {type === "document" && docTitle && (
                    <div className="mt-4 flex items-center gap-2 rounded-xl border border-brand-blue/15 bg-brand-blue/5 px-4 py-3 text-sm font-medium text-brand-blue" data-testid="lead-document-badge">
                      <FileText className="h-4 w-4 shrink-0 text-brand-red" />
                      {docTitle}
                    </div>
                  )}

                  {type !== "document" && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {CTA_OPTIONS.map((o) => (
                        <button
                          key={o.type}
                          onClick={() => setType(o.type)}
                          className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                            type === o.type
                              ? "bg-brand-blue text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                          data-testid={`lead-type-${o.type}`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <form onSubmit={submit} className="mt-5 space-y-4">
                    {type !== "document" && (
                      <div>
                        <Label htmlFor="lead-formation">Formation souhaitée</Label>
                        <select
                          id="lead-formation"
                          name="formation"
                          value={formation}
                          onChange={(e) => setFormation(e.target.value)}
                          className="mt-1.5 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-brand-blue ring-offset-white transition-colors focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
                          data-testid="lead-select-formation"
                        >
                          <option value="">Sélectionnez une formation…</option>
                          {CATEGORIES.filter((cat) => !centreCategories || centreCategories.has(cat.slug)).map((cat) => (
                            <optgroup key={cat.slug} label={cat.title}>
                              {getFormationsByCategory(cat.slug).map((f) => (
                                <option key={f.slug} value={f.title}>
                                  {f.title}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="lead-centre">Centre concerné{type === "document" ? " *" : ""}</Label>
                      <select
                        id="lead-centre"
                        name="centre"
                        value={centre}
                        onChange={(e) => setCentre(e.target.value)}
                        className="mt-1.5 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-brand-blue ring-offset-white transition-colors focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
                        data-testid="lead-select-centre"
                      >
                        <option value="">Sélectionnez un centre…</option>
                        {compatibleCentres.map((c) => (
                          <option key={c.id} value={c.ville}>
                            {c.ville}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="lead-objet">Objet</Label>
                      <select
                        id="lead-objet"
                        name="objet"
                        value={objet}
                        onChange={(e) => setObjet(e.target.value)}
                        className="mt-1.5 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-brand-blue ring-offset-white transition-colors focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
                        data-testid="lead-select-objet"
                      >
                        <option value="">Sélectionnez un objet…</option>
                        <option value="Renseignement">Renseignement</option>
                        <option value="Inscription">Inscription</option>
                        <option value="Devis">Devis</option>
                        <option value="Financement">Financement</option>
                        <option value="Accessibilité / Handicap">Accessibilité / Handicap</option>
                        <option value="Rappel téléphonique">Rappel téléphonique</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="lead-nom">Nom complet *</Label>
                      <Input id="lead-nom" name="nom" value={form.nom} onChange={handleChange}
                        placeholder="Votre nom" className="mt-1.5" data-testid="lead-input-nom" />
                    </div>
                    <div>
                      <Label htmlFor="lead-tel">Téléphone</Label>
                      <Input id="lead-tel" name="telephone" value={form.telephone}
                        onChange={handleChange} placeholder="06 00 00 00 00" className="mt-1.5"
                        data-testid="lead-input-telephone" />
                    </div>
                    <div>
                      <Label htmlFor="lead-email">{type === "document" ? "Email *" : "Email"}</Label>
                      <Input id="lead-email" name="email" type="email" value={form.email}
                        onChange={handleChange} placeholder="email@exemple.fr" className="mt-1.5"
                        data-testid="lead-input-email" />
                    </div>
                    <div>
                      <Label htmlFor="lead-msg">Message</Label>
                      <Textarea id="lead-msg" name="message" value={form.message} onChange={handleChange}
                        placeholder="Votre message (facultatif)" className="mt-1.5" rows={3}
                        data-testid="lead-input-message" />
                    </div>
                    <Button type="submit" disabled={submitting}
                      className="group w-full rounded-full bg-brand-red py-6 text-base font-semibold hover:bg-brand-red-light"
                      data-testid="lead-submit">
                      {submitting ? "Envoi..." : type === "document" ? "Recevoir le document" : "Envoyer ma demande"}
                      <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                    <p className="text-center text-[11px] leading-relaxed text-slate-400">
                      En envoyant ce formulaire, vous acceptez que vos données soient transmises au
                      centre EDUCASER GROUP concerné pour le traitement de votre demande.
                      Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de
                      suppression en écrivant à contact@educaser.fr.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LeadContext.Provider>
  );
}
